/**
 * @file edit.tsx
 *
 * @fileoverview Helper methods for editing a job.
 */
import { FormattedMessage, useIntl } from 'react-intl';
import { getCurrentFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { LockIcon } from '~/vcl/icons/Lock';
import FormItemsWidget from '../../Form/Widgets/Form/FormItems';
import { Text } from '~/vcl/atoms/13-text/03-text';

interface HiddenFields {
  [key: string]: React.ReactNode;
}

/**
 * Helper method that returns the list of fields overrides that should be disabled for when
 * editing a job that is already published.
 *
 * @param {boolean} isEditPage
 *   If the current page is the "job edit" page.
 * @param {boolean} isJobPublished
 *   True if the job is published (Workflow is published)
 * @param {string} jobType
 *   The job type.
 *
 * @returns {HiddenFields}
 *   A list of all fields to override in the job edit form.
 */
export const assignFieldsToHide = (
  isEditPage: boolean,
  isJobPublished: boolean,
  jobType: string,
) => {
  const intl = useIntl();
  const editFieldsToHide: HiddenFields = {};
  const fieldsToHideOnEdit =
    getCurrentFrontendConfig().JOBS.JOB_EDIT_HIDDEN_FIELDS || [];
  if (isEditPage && isJobPublished) {
    fieldsToHideOnEdit.forEach((fieldToHide) => {
      Object.assign(editFieldsToHide, {
        [fieldToHide]: ({ field }) => {
          return (
            <div key={fieldToHide}>
              <FormItemsWidget
                name="node"
                bundle={jobType}
                fields={[Object.assign({}, field, { disabled: true })]}
              />
              <Spacing size="medium" />
              <FlexItems alignItems="center">
                <FlexItem marginRight={5}>
                  <LockIcon
                    title={intl.formatMessage({
                      id: 'job_edit_hidden_field_info_icon',
                      defaultMessage: 'Info',
                    })}
                    color="warning"
                  />
                </FlexItem>
                <FlexItem>
                  <Text tag="span" color="gray6" fontSize={2}>
                    <FormattedMessage
                      id="job_edit_hidden_field_info"
                      defaultMessage="The field {fieldLabel} can not be edited. To change the field you need to republish the job."
                      values={{
                        fieldLabel: <strong>{field.label}</strong>,
                      }}
                    />
                  </Text>
                </FlexItem>
              </FlexItems>
              <Spacing size="medium" />
            </div>
          );
        },
      });
    });
  }

  return editFieldsToHide;
};
